import { Injector, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SubscriptionBillingLockService } from 'src/app/private/services/subscription-billing-lock.service';

export type MailHubMailboxProvider = 'outlook' | 'gmail';
export type EmailPreferenceProvider = MailHubMailboxProvider | 'ses';

/** Stored in localStorage / users.commincation_pref — supports both OAuth mailboxes at once. */
export interface CommunicationPrefStored {
  v: 2;
  activeProvider: EmailPreferenceProvider | null;
  mailboxes: Partial<Record<EmailPreferenceProvider, { email: string }>>;
}

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  isLogin = false;
  isCenter = false;
  clientLogo: any;
  role: any;
  profile_name: any;
  userId: any;
  hospital_id: any;
  center_id: any;
  client_id: any;
  dashboard: any;
  specialty: any;
  permissions: any;
  appointment_config: any;
  leadChannels: any;
  feature_modules: any;
  is_fixed_col: any;
  get_location: any;
  woocomerce_cred: any;
  commincation_pref: any;
  clientName: any;

  constructor(
    private router: Router,
    private injector: Injector,
  ) {}

  /**
   * HttpClient may receive the login user object directly, or wrapped (e.g. API Gateway
   * `{ statusCode, body: "<json string>" }`, or `{ data: { ... } }`). The Network tab
   * “Preview” can show the inner JSON while `resp` in code is still the wrapper — then
   * `resp.role` is undefined and `resp.role.role_name` throws.
   */
  private serializeFeatureModules(value: any): string {
    if (value == null || value === '') {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }

  private normalizeLoginResponse(raw: any): any {
    if (raw == null || typeof raw !== 'object') {
      return {};
    }
    let cur: any = raw;
    for (let depth = 0; depth < 8; depth++) {
      if (cur.role != null && typeof cur.role === 'object') {
        return cur;
      }
      if (cur.token != null || cur.user_id != null) {
        return cur;
      }
      if (cur.data != null && typeof cur.data === 'object') {
        cur = cur.data;
        continue;
      }
      if (cur.body != null) {
        if (typeof cur.body === 'string') {
          try {
            cur = JSON.parse(cur.body);
            continue;
          } catch {
            return raw;
          }
        }
        if (typeof cur.body === 'object') {
          cur = cur.body;
          continue;
        }
      }
      break;
    }
    return cur;
  }

  login(resp: any) {
    //  console.log(resp)
    const payload = this.normalizeLoginResponse(resp);
    this.isLogin = true;
    const roleName =
      payload?.role?.role_name ??
      (typeof payload?.role_name === 'string' ? payload.role_name : '');
    this.role = roleName;
    const client = payload?.client;
    localStorage.setItem('leadChannels', client?.leadChannels || null);
    localStorage.setItem(
      'appointment_config',
      payload.appointment_config || null,
    );
    localStorage.setItem('isproblemArea', payload.isproblemArea || null);
    localStorage.setItem('center_id', payload.center_id ?? '');
    localStorage.setItem('dept_id', payload.dept_id);
    localStorage.setItem('role_id', payload.role_id);
    localStorage.setItem('client_id', payload.client_id);
    localStorage.setItem(
      'embed_token',
      payload.embed_token ?? client?.embed_token ?? '',
    );
    localStorage.setItem('is_cron', client?.isCron ?? '');
    localStorage.setItem('user_id', payload.user_id);
    const profileName = `${payload.fname ?? ''} ${payload.lname ?? ''}`.trim();
    localStorage.setItem('profile_name', profileName);
    localStorage.setItem('prefix', client?.prefix ?? '');
    localStorage.setItem('fname', payload.fname);
    localStorage.setItem('lname', payload.lname);
    localStorage.setItem('role', roleName);
    localStorage.setItem('clientLogo', client?.logo ?? '');
    localStorage.setItem(
      'feature_modules',
      this.serializeFeatureModules(client?.feature_modules),
    );
    localStorage.setItem('specialty', client?.specialty ?? '');
    localStorage.setItem('dateFormat', client?.dateFormat ?? '');
    localStorage.setItem(
      'permissions',
      JSON.stringify(payload?.role?.permissions ?? []),
    );
    localStorage.setItem('enablePrintLayout', client?.enablePrintLayout ?? '');
    localStorage.setItem('emailId', payload.email);
    localStorage.setItem('isCenter', client?.is_center ?? '');
    localStorage.setItem('is_fixed_col', client?.is_fixed_col ?? '');
    localStorage.setItem('get_location', client?.get_location ?? '');
    localStorage.setItem('user_sign', JSON.stringify(payload.user_sign));
    localStorage.setItem(
      'woocomerce_cred',
      JSON.stringify(client?.woocomerce_cred ?? null),
    );
    localStorage.setItem('currency', client?.other_settings?.currency);
    const commNorm = this.normalizeCommPrefForStorage(
      payload.commincation_pref,
    );
    localStorage.setItem(
      'commincation_pref',
      commNorm == null ? '' : JSON.stringify(commNorm),
    );
    localStorage.setItem('clientName', client?.name ?? '');

    // Apply white label settings from client.other_settings.white_label if present
    if (client?.other_settings?.white_label) {
      try {
        localStorage.setItem(
          'crm_white_label_theme',
          JSON.stringify(client.other_settings.white_label),
        );
      } catch {
        /* ignore quota errors */
      }
    }

    const rawUser = payload.username != null ? String(payload.username) : '';
    const username = rawUser.replace(/([A-Z])/g, ' $1').trim();

    localStorage.setItem('username', username);
    if (client?.logo) {
      this.toDataUrl(client.logo, this.getBase64Logo);
    }

    if (client?.enablePrintLayout && client?.printlayout) {
      this.toDataUrl(
        client.printlayout.pre_header_image,
        this.getBase64HeaderPrescription,
      );
      this.toDataUrl(
        client.printlayout.pre_footer_image,
        this.getBase64FooterPrescription,
      );
    }
    try {
      this.injector.get(SubscriptionBillingLockService).resetLockState();
    } catch {
      /* tests or early bootstrap */
    }
    return { success: this.isLogin, role: this.role };
  }

  logout() {
    this.isLogin = false;
    this.role = '';
    localStorage.setItem('leadChannels', '');
    localStorage.setItem('appointment_config', '');
    localStorage.setItem('isproblemArea', '');
    localStorage.setItem('center_id', '');
    localStorage.setItem('dept_id', '');
    localStorage.setItem('client_id', '');
    localStorage.setItem('role_id', '');
    localStorage.setItem('client_Id', '');
    localStorage.setItem('user_id', '');
    localStorage.setItem('profile_name', '');
    localStorage.setItem('role', '');
    localStorage.setItem('permissions', '');
    localStorage.setItem('specialty', '');
    localStorage.setItem('token', '');
    localStorage.setItem('username', '');
    localStorage.setItem('enablePrintLayout', '');
    localStorage.setItem('getBase64HeaderPrescription', '');
    localStorage.setItem('getBase64FooterPrescription', '');
    localStorage.setItem('getBase64logo', '');
    localStorage.setItem('clientLogo', '');
    localStorage.setItem('feature_modules', '');
    localStorage.setItem('isCenter', '');
    localStorage.setItem('is_fixed_col', '');
    localStorage.setItem('get_location', '');
    localStorage.setItem('woocomerce_cred', '');
    localStorage.setItem('user_sign', '');
    localStorage.setItem('commincation_pref', '');
    localStorage.setItem('clientName', '');
    localStorage.setItem('is_employee', '');
    localStorage.setItem('employee_id', '');
    localStorage.setItem('employee_code', '');
    localStorage.setItem('employee_info', '');
    try {
      this.injector.get(SubscriptionBillingLockService).clear();
    } catch {
      /* tests */
    }
    return { success: this.isLogin, role: '' };
  }

  /* ── Employee Enrollment Helpers (for My Desk) ─────────────────────────── */

  isEnrolledEmployee(): boolean {
    return localStorage.getItem('is_employee') === 'true';
  }

  setEmployeeEnrollment(status: boolean, empId?: any, empCode?: string, employeeData?: any): void {
    localStorage.setItem('is_employee', status ? 'true' : 'false');
    if (empId) localStorage.setItem('employee_id', String(empId));
    if (empCode) localStorage.setItem('employee_code', String(empCode));
    if (employeeData) {
      try {
        localStorage.setItem('employee_info', JSON.stringify(employeeData));
      } catch (_) {}
    }
  }

  getEmployeeId(): string {
    return localStorage.getItem('employee_id') || this.getUserId() || '';
  }

  getEmployeeCode(): string {
    return localStorage.getItem('employee_code') || '';
  }

  getEmployeeInfo(): any {
    try {
      const raw = localStorage.getItem('employee_info');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  getClientName() {
    this.clientName = localStorage.getItem('clientName') || '';
    return this.clientName;
  }

  getWoocomerceCred() {
    this.woocomerce_cred = localStorage.getItem('woocomerce_cred') || {};
    return this.woocomerce_cred;
  }
  getIsGetLocation() {
    this.get_location =
      localStorage.getItem('get_location') == 'true' ? true : false;
    return this.get_location;
  }
  getIsFixedCol() {
    const val = localStorage.getItem('is_fixed_col');
    // Default to true for new clients where value is null, 'null' string, or empty
    this.is_fixed_col =
      val === null || val === '' || val === 'null' || val === 'true';
    return this.is_fixed_col;
  }
  private readCommPrefRaw(): any | null {
    const raw = localStorage.getItem('commincation_pref');
    if (!raw || raw === 'null') {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Normalize DB/login/API shapes into v2 inner storage.
   * Legacy: `{ provider, email }` (single mailbox).
   */
  normalizeCommPrefForStorage(raw: any): CommunicationPrefStored | null {
    if (raw == null || raw === '') {
      return null;
    }
    const obj =
      typeof raw === 'string'
        ? (() => {
            try {
              return JSON.parse(raw);
            } catch {
              return null;
            }
          })()
        : raw;
    return this.parseToStoredCommPref(obj);
  }

  private parseToStoredCommPref(parsed: any): CommunicationPrefStored | null {
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (
      parsed.v === 2 &&
      parsed.mailboxes &&
      typeof parsed.mailboxes === 'object'
    ) {
      const mailboxes: CommunicationPrefStored['mailboxes'] = {};
      if (parsed.mailboxes.outlook?.email) {
        mailboxes.outlook = {
          email: String(parsed.mailboxes.outlook.email).trim(),
        };
      }
      if (parsed.mailboxes.gmail?.email) {
        mailboxes.gmail = {
          email: String(parsed.mailboxes.gmail.email).trim(),
        };
      }
      if (parsed.mailboxes.ses?.email) {
        mailboxes.ses = { email: String(parsed.mailboxes.ses.email).trim() };
      }
      const ap = parsed.activeProvider;
      const activeProvider: EmailPreferenceProvider | null =
        ap === 'outlook' || ap === 'gmail' || ap === 'ses' ? ap : null;
      if (!mailboxes.outlook && !mailboxes.gmail && !mailboxes.ses) {
        if (activeProvider) {
          return { v: 2, activeProvider, mailboxes: {} };
        }
        return null;
      }
      return { v: 2, activeProvider, mailboxes };
    }
    if (parsed.email && parsed.provider) {
      const p = String(parsed.provider).toLowerCase();
      if (p === 'outlook' || p === 'gmail' || p === 'ses') {
        return {
          v: 2,
          activeProvider: p,
          mailboxes: { [p]: { email: String(parsed.email).trim() } },
        };
      }
    }
    return null;
  }

  private writeStoredCommPref(stored: CommunicationPrefStored | null): void {
    if (!stored) {
      localStorage.setItem('commincation_pref', '');
      return;
    }
    const hasOutlook = Boolean(stored.mailboxes?.outlook?.email);
    const hasGmail = Boolean(stored.mailboxes?.gmail?.email);
    const hasSes = Boolean(stored.mailboxes?.ses?.email);
    if (!hasOutlook && !hasGmail && !hasSes) {
      if (
        stored.activeProvider === 'outlook' ||
        stored.activeProvider === 'gmail' ||
        stored.activeProvider === 'ses'
      ) {
        localStorage.setItem(
          'commincation_pref',
          JSON.stringify({
            v: 2,
            activeProvider: stored.activeProvider,
            mailboxes: {},
          }),
        );
        return;
      }
      localStorage.setItem('commincation_pref', '');
      return;
    }
    const payload: CommunicationPrefStored = {
      v: 2,
      activeProvider: stored.activeProvider ?? null,
      mailboxes: { ...stored.mailboxes },
    };
    localStorage.setItem('commincation_pref', JSON.stringify(payload));
  }

  /** Merge/update one OAuth mailbox; does not change `activeProvider` (Mail Hub switch sets that). */
  mergeMailboxConnection(
    provider: MailHubMailboxProvider,
    email: string,
  ): void {
    const trimmed = String(email || '').trim();
    if (!trimmed) {
      return;
    }
    const prev = this.parseToStoredCommPref(this.readCommPrefRaw()) || {
      v: 2 as const,
      activeProvider: null,
      mailboxes: {},
    };
    prev.v = 2;
    prev.mailboxes = { ...prev.mailboxes, [provider]: { email: trimmed } };
    this.writeStoredCommPref(prev);
  }

  /** Remove one mailbox; clears `activeProvider` if it pointed at it; picks another active if any left. */
  removeMailboxConnection(
    provider: MailHubMailboxProvider,
  ): CommunicationPrefStored | null {
    const prev = this.parseToStoredCommPref(this.readCommPrefRaw());
    if (!prev?.mailboxes?.[provider]) {
      this.writeStoredCommPref(prev);
      return prev;
    }
    const rest: CommunicationPrefStored['mailboxes'] = { ...prev.mailboxes };
    delete rest[provider];
    let activeProvider: EmailPreferenceProvider | null =
      prev.activeProvider === provider ? null : prev.activeProvider;
    if (!activeProvider) {
      if (rest.outlook?.email) {
        activeProvider = 'outlook';
      } else if (rest.gmail?.email) {
        activeProvider = 'gmail';
      } else if (rest.ses?.email) {
        activeProvider = 'ses';
      }
    }
    const next: CommunicationPrefStored = {
      v: 2,
      mailboxes: rest,
      activeProvider,
    };
    const stillHas = Boolean(
      rest.outlook?.email || rest.gmail?.email || rest.ses?.email,
    );
    this.writeStoredCommPref(stillHas ? next : null);
    return stillHas ? next : null;
  }

  /** Called when user picks Outlook vs Gmail in Sales Mail Hub (drives API email + DB sync). */
  setActiveMailboxProvider(provider: MailHubMailboxProvider): void {
    const prev = this.parseToStoredCommPref(this.readCommPrefRaw()) || {
      v: 2 as const,
      activeProvider: provider,
      mailboxes: {},
    };
    prev.v = 2;
    prev.activeProvider = provider;
    this.writeStoredCommPref(prev);
  }

  hasMailboxConnection(provider: MailHubMailboxProvider): boolean {
    const inner = this.parseToStoredCommPref(this.readCommPrefRaw());
    return Boolean(inner?.mailboxes?.[provider]?.email);
  }

  getMailboxEmail(provider: MailHubMailboxProvider): string {
    const inner = this.parseToStoredCommPref(this.readCommPrefRaw());
    return (inner?.mailboxes?.[provider]?.email || '').trim();
  }

  /** Payload for `users.updateUser({ commincation_pref })` — no derived fields. */
  getCommPrefPayloadForUser(): object {
    const inner = this.parseToStoredCommPref(this.readCommPrefRaw());
    if (!inner) {
      return {};
    }
    return {
      v: 2,
      activeProvider: inner.activeProvider ?? null,
      mailboxes: { ...inner.mailboxes },
    };
  }

  /**
   * Read pref with backward-compatible `provider` + `email` (derived from Mail Hub session, then active, then any mailbox).
   * Used by sequences, lead email, etc.
   */
  getCommincationPref(): any | null {
    const inner = this.parseToStoredCommPref(this.readCommPrefRaw());
    if (
      !inner ||
      (!inner.mailboxes?.outlook &&
        !inner.mailboxes?.gmail &&
        !inner.mailboxes?.ses)
    ) {
      return null;
    }
    let hub: string | null = null;
    try {
      hub = sessionStorage.getItem('sales_mail_hub_mail_provider');
    } catch {
      /* non-browser */
    }
    const hubProvider: MailHubMailboxProvider | null =
      hub === 'outlook' || hub === 'gmail' ? hub : null;
    let pick: EmailPreferenceProvider | null =
      hubProvider && inner.mailboxes[hubProvider]?.email
        ? hubProvider
        : (inner.activeProvider ?? null);
    let email = pick ? inner.mailboxes[pick]?.email : undefined;
    if (!email) {
      pick = inner.mailboxes.outlook?.email
        ? 'outlook'
        : inner.mailboxes.gmail?.email
          ? 'gmail'
          : inner.mailboxes.ses?.email
            ? 'ses'
            : null;
      email = pick ? inner.mailboxes[pick]?.email : undefined;
    }
    return {
      ...inner,
      provider: pick,
      email: email ?? null,
    };
  }

  setCommincationPref(data: any) {
    if (
      data == null ||
      data === '' ||
      (typeof data === 'object' && Object.keys(data).length === 0)
    ) {
      localStorage.setItem('commincation_pref', '');
      return;
    }
    if (data.v === 2 && data.mailboxes && typeof data.mailboxes === 'object') {
      const norm = this.parseToStoredCommPref(data);
      this.writeStoredCommPref(norm);
      return;
    }
    if (data.provider && data.email) {
      const p = String(data.provider).toLowerCase();
      if (p === 'outlook' || p === 'gmail') {
        this.mergeMailboxConnection(p, data.email);
        return;
      }
      if (p === 'ses') {
        const previous = this.parseToStoredCommPref(this.readCommPrefRaw());
        this.writeStoredCommPref({
          v: 2,
          activeProvider: 'ses',
          mailboxes: {
            ...(previous?.mailboxes || {}),
            ses: { email: String(data.email).trim() },
          },
        });
        return;
      }
    }
    localStorage.setItem('commincation_pref', JSON.stringify(data));
  }
  getClientLogo() {
    return localStorage.getItem('clientLogo');
  }

  getCron() {
    return localStorage.getItem('is_cron');
  }
  getIsCenter() {
    if (localStorage.getItem('isCenter') == 'True') this.isCenter = true;
    else this.isCenter = false;
    return this.isCenter;
  }
  getEmail() {
    return localStorage.getItem('emailId');
  }

  /**
   * Mailbox identity for Sales Mail Hub API calls. Prefer the OAuth-linked address from
   * `commincation_pref` when provider is Gmail or Outlook; otherwise use the account login
   * email (`emailId`). Avoids using SES / transactional addresses as the hub user key.
   */
  getMailHubUserEmail(): string {
    const accountEmail = (this.getEmail() || '').trim();
    const inner = this.parseToStoredCommPref(this.readCommPrefRaw());
    if (!inner) {
      return accountEmail;
    }
    let hub: string | null = null;
    try {
      hub = sessionStorage.getItem('sales_mail_hub_mail_provider');
    } catch {
      /* ignore */
    }
    if (hub === 'outlook' || hub === 'gmail') {
      const em = inner.mailboxes[hub]?.email;
      if (em) {
        return em.trim();
      }
    }
    if (
      inner.activeProvider === 'outlook' ||
      inner.activeProvider === 'gmail'
    ) {
      const em = inner.mailboxes[inner.activeProvider]?.email;
      if (em) {
        return em.trim();
      }
    }
    const fallback =
      inner.mailboxes.outlook?.email || inner.mailboxes.gmail?.email || '';
    return fallback.trim() || accountEmail;
  }

  /**
   * API payload value for quote/invoice/sequence/lead sends (matches part-c / part-b expectations).
   */
  getEmailProviderTypeForPayload(): 'OUTLOOK' | 'GMAIL' | 'SES' | '' {
    const pref = this.getCommincationPref();
    const p = pref?.provider;
    if (p === 'gmail') return 'GMAIL';
    if (p === 'outlook') return 'OUTLOOK';
    if (p === 'ses') return 'SES';
    return '';
  }

  /**
   * `from_email` / OAuth mailbox for sends: linked Outlook/Gmail for hub providers, else comm pref or login email.
   */
  getSendEmailFromAddress(): string {
    const t = this.getEmailProviderTypeForPayload();
    if (t === 'OUTLOOK' || t === 'GMAIL') {
      return this.getMailHubUserEmail();
    }
    if (!t) {
      return '';
    }
    const pref = this.getCommincationPref();
    return (pref?.email || '').trim();
  }

  isLoggedIn() {
    const loggedIn = localStorage.getItem('isLogin');
    if (loggedIn == 'true') this.isLogin = true;
    else this.isLogin = false;
    return this.isLogin;
  }

  getLeadChannels() {
    this.leadChannels = localStorage.getItem('leadChannels');
    return this.leadChannels;
  }
  getFeatureModules() {
    this.feature_modules = localStorage.getItem('feature_modules');
    return this.feature_modules;
  }
  getRole() {
    this.role = localStorage.getItem('role');
    return this.role;
  }
  getSpecialty() {
    this.specialty = localStorage.getItem('specialty');
    return this.specialty;
  }
  getPermissions() {
    this.permissions = localStorage.getItem('permissions');
    return this.permissions;
  }
  getHospitalId() {
    this.hospital_id = localStorage.getItem('hospital_id');
    return this.hospital_id;
  }
  getCenterId() {
    const val = localStorage.getItem('center_id');
    // Guard against the string "null" being stored from a null value
    this.center_id = val === null || val === '' || val === 'null' ? null : val;
    return this.center_id;
  }
  getClientId() {
    this.client_id = localStorage.getItem('client_id');
    return this.client_id;
  }
  getSignatures() {
    let singatures = localStorage.getItem('user_sign');
    return JSON.parse(singatures || '[]');
  }

  getProfileName() {
    this.profile_name = localStorage.getItem('profile_name');
    return this.profile_name;
  }

  getPrefix() {
    let prefix = localStorage.getItem('prefix');
    return prefix;
  }

  getFirstName() {
    let first_name = localStorage.getItem('fname');
    return first_name;
  }

  getLastName() {
    let last_name = localStorage.getItem('lname');
    return last_name;
  }

  getUserId() {
    this.userId = localStorage.getItem('user_id') || null;
    return this.userId;
  }

  getUser() {
    const user = {
      user_id: localStorage.getItem('user_id'),
      fname: localStorage.getItem('fname'),
      lname: localStorage.getItem('lname'),
    };
    return user;
  }

  getAppointment_config() {
    this.appointment_config = localStorage.getItem('appointment_config');
    return this.appointment_config;
  }
  getDashboard() {
    this.dashboard = localStorage.getItem('dashboards')?.split(',');
    return this.dashboard;
  }

  getDashboardAccess(dashboardName: any) {
    let dashboard: any = localStorage.getItem('dashboards')?.split(',');
    console.log(dashboardName + '--' + dashboard);
    let isAuthenticated = false;
    if (dashboard) {
      return (isAuthenticated = dashboard.includes(dashboardName));
    }
  }
  getBase64HeaderPrescription(data: any) {
    localStorage.setItem('getBase64HeaderPrescription', data);
  }
  getBase64FooterPrescription(data: any) {
    localStorage.setItem('getBase64FooterPrescription', data);
  }
  getBase64Logo(data: any) {
    localStorage.setItem('getBase64logo', data);
  }

  getClientCurrency() {
    console.log(localStorage.getItem('currency'));
    return localStorage.getItem('currency');
  }

  toDataUrl(url: any, callback: any) {
    var xhr = new XMLHttpRequest();
    xhr.onload = function () {
      var reader = new FileReader();
      reader.onloadend = function () {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  }

  checkFeatures(module: any) {
    let features = JSON.parse(JSON.stringify(this.feature_modules));
    return features.includes(module) ? true : false;
  }

  checkCRUDAccess(module: any, submodule: any) {
    let accessObj = {
      title: '',
      name: '',
      active: false,
      update: false,
      export: false,
      import: false,
      create: false,
      delete: false,
    };
    let features = JSON.parse(this.getPermissions()).find((item: any) => {
      console.log('module_name', item.module_name);
      return item.module_name == module;
    });
    if (features) {
      accessObj = {
        ...accessObj,
        ...features['features'].find((item: any) => {
          console.log('submodule', item.name);
          return item.name == submodule;
        }),
      };
    }

    return accessObj;
  }

  checkUserAccess(module_name: any, feature_name: any) {
    //console.log(this.permissions)
    let feature = [];
    let module = [];
    module = this.permissions.filter((item: any) => {
      return item.module_name == module_name;
    });
    if (module.length > 0) {
      feature = module[0]['features'].filter((item: any) => {
        return item.name == feature_name;
      });
    }

    return module[0] && feature[0] ? true : false;
  }

  is_admin() {
    return this.role == 'admin' || this.role == 'Administrator';
  }
  is_root() {
    return this.role == 'root' || this.role == 'superadmin';
  }

  checkFullAccessForTableList(role: any) {
    const roles = [
      'Administrator',
      'Root',
      'Superadmin',
      'Admin',
      'admin',
      'administrator',
    ];
    return roles.includes(role);
  }
}
